import Icon from '@ant-design/icons';
import DeleteOutlined from '@ant-design/icons/lib/icons/DeleteOutlined';
import { Button, Checkbox, Col, DatePicker, Form, FormInstance, Input, message, Row, Select } from 'antd';
import moment from 'moment';
import React, { Component } from 'react';
import { FormattedMessage, injectIntl, WrappedComponentProps } from 'react-intl';
import { Link, RouteComponentProps } from 'react-router-dom';
import awardApi from '../../../../apis/AwardApi';
import userApi from '../../../../apis/UserApi';
import LayoutComponent from '../../../../components/LayoutComponent/LayoutComponent';
import CustomContext from '../../../../context/CustomContext';
import { Award, FamilyMember } from '../../../../model/Entities';
import { awards } from '../../../../model/Types';
import { ReactComponent as SaveSvg } from '../../../../resources/images/ico-save.svg';
import HeadMetadata from '../../../../services/HeadMetadata';
import notificationService from '../../../../services/NotificationService';
import styles from './AdminAwardPage.module.scss';

class AdminAwardPage extends Component<Props, State> {
    static contextType = CustomContext;
    context!: React.ContextType<typeof CustomContext>;
    formRef = React.createRef<FormInstance>();

    constructor(props: Props) {
        super(props);
        this.state = { users: [] };
    }

    componentDidMount() {
        this.init();
    }

    /** METHODS **/

    init = async () => {
        try {
            this.setState({ loading: true });
            if (this.props.match.params.id === 'new') {
                await this.new();
            } else {
                await this.get(this.props.match.params.id);
            }
        } catch (error) {
            notificationService.displayError(error, this.props.intl);
        } finally {
            this.setState({ loading: false });
        }
    };

    new = async () => {
        const award: Award = {
            name: '',
        };
        const usersPage = await userApi.list(20, 1);
        this.setState({ award, users: usersPage.items });
        this.formRef.current!.setFieldsValue(award);
    };

    get = async (id: string) => {
        const award = await awardApi.get(id);
        const usersPage = await userApi.list(20, 1);
        const users = usersPage.items;
        if (award.userId) {
            const user = await userApi.get(award.userId);
            users.push(user);
        }
        const values: any = Object.assign({}, award);
        values.date = moment.utc(values.date).local();

        this.setState({ award, users });
        this.formRef.current!.setFieldsValue(values);
    };

    save = async (values: any) => {
        try {
            this.setState({ loading: true });
            let award: Award = Object.assign({}, this.state.award, values);
            award.date = values.date ? values.date.valueOf() : undefined;
            award = award.id ? await awardApi.update(award) : await awardApi.create(award);
            message.success(this.props.intl.formatMessage({ id: 'status.saved' }));

            this.setState({ award });
        } catch (error) {
            notificationService.displayError(error, this.props.intl, [
                { status: 409, message: 'award.status.duplicate' },
            ]);
        } finally {
            this.setState({ loading: false });
        }
    };

    delete = async () => {
        const { award } = this.state;
        try {
            this.setState({ loading: true });
            await awardApi.delete(award!);
            this.props.history.push(`/admin/awards`);
        } catch (error) {
            notificationService.displayError(error, this.props.intl);
        } finally {
            this.setState({ loading: false });
        }
    };

    searchUsers = async (searchText: string) => {
        try {
            const usersPage = await userApi.listByRole(20, 1, ['givenName'], 'asc', searchText);
            const users = usersPage.items;
            this.setState({ users });
        } catch (error) {
            notificationService.displayError(error, this.props.intl);
        }
    };

    /*** COMPONENTS ***/

    renderContent = (): React.ReactElement | undefined => {
        const { loading, users } = this.state;

        const userOptions = users.map((user) => (
            <Select.Option key={user.userId!} value={user.userId!}>
                {user.givenName} {user.familyName}
            </Select.Option>
        ));

        const awardsOptions = awards.map((award) => (
            <Select.Option key={award} value={award}>
                {award}
            </Select.Option>
        ));

        return (
            <>
                {this.renderToolbar()}
                <Form ref={this.formRef} onFinish={this.save} colon={false} layout="vertical">
                    <Row gutter={[28, 0]}>
                        <Col xs={24} md={8}>
                            <Form.Item
                                label={<FormattedMessage id="award.name" />}
                                name="name"
                                rules={[{ required: true, message: <FormattedMessage id="status.mandatory" /> }]}
                            >
                                <Input maxLength={128} size="large" />
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={8}>
                            <Form.Item
                                label={<FormattedMessage id="award.type" />}
                                name="type"
                                rules={[
                                    {
                                        required: true,
                                        message: <FormattedMessage id="status.mandatory" />,
                                    },
                                ]}
                            >
                                <Select
                                    className={styles.select}
                                    placeholder={<FormattedMessage id="award.chooseType" />}
                                    style={{ width: 350 }}
                                >
                                    {awardsOptions}
                                </Select>
                            </Form.Item>
                        </Col>
                    </Row>
                    <Row gutter={[28, 0]}>
                        <Col xs={24} md={8}>
                            <Form.Item
                                name="display"
                                rules={[{ required: true, message: <FormattedMessage id="status.mandatory" /> }]}
                                valuePropName="checked"
                            >
                                <Checkbox>
                                    <FormattedMessage id="award.display" />
                                </Checkbox>
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={8}>
                            <Form.Item name="favorite" valuePropName="checked">
                                <Checkbox>
                                    <FormattedMessage id="award.favorite" />
                                </Checkbox>
                            </Form.Item>
                        </Col>
                    </Row>
                    <Row gutter={[28, 0]}>
                        <Col xs={24} md={8}>
                            <Form.Item label={<FormattedMessage id="award.description" />} name="description">
                                <Input size="large" />
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={8}>
                            <Form.Item label={<FormattedMessage id="award.url" />} name="url">
                                <Input size="large" />
                            </Form.Item>
                        </Col>
                    </Row>
                    <Row gutter={[28, 0]}>
                        <Col xs={24} md={8}>
                            <Form.Item label={<FormattedMessage id="award.date" />} name="date">
                                <DatePicker size="large" format="ll" />
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={8}>
                            <Form.Item label={<FormattedMessage id="award.combineId" />} name="combineId">
                                <Input size="large" />
                            </Form.Item>
                        </Col>
                    </Row>
                    <Row gutter={[28, 0]}>
                        <Col xs={24} md={8}>
                            <Form.Item label={<FormattedMessage id="award.endorsementCount" />} name="endorsementCount">
                                <Input size="large" />
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={8}>
                            <Form.Item
                                label={<FormattedMessage id="award.userId" />}
                                name="userId"
                                rules={[{ required: true, message: <FormattedMessage id="status.mandatory" /> }]}
                            >
                                <Select size="large" showSearch filterOption={false} onSearch={this.searchUsers}>
                                    {userOptions}
                                </Select>
                            </Form.Item>
                        </Col>
                    </Row>

                    <div className={styles.buttons}>
                        <Link to="/admin/awards">
                            <Button size="large" className={styles.back}>
                                <FormattedMessage id="award.back" />
                            </Button>
                        </Link>
                        {this.props.match.params.id !== 'new' && (
                            <Button
                                size="large"
                                icon={<DeleteOutlined />}
                                className={styles.delete}
                                onClick={() => this.delete()}
                            >
                                <FormattedMessage id="award.delete" />
                            </Button>
                        )}
                        <Button
                            type="primary"
                            htmlType="submit"
                            size="large"
                            className={styles.save}
                            icon={<Icon component={SaveSvg} />}
                            loading={loading}
                        >
                            <FormattedMessage id="award.save" />
                        </Button>
                    </div>
                </Form>
            </>
        );
    };

    renderToolbar = (): React.ReactElement | undefined => {
        if (this.props.match.params.id === 'new') {
            return (
                <div className={styles.toolbar}>
                    <h1>
                        <FormattedMessage id="awards.addtitle" />
                    </h1>
                </div>
            );
        } else {
            return (
                <div className={styles.toolbar}>
                    <h1>
                        <FormattedMessage id="awards.edittitle" />
                    </h1>
                </div>
            );
        }
    };

    render() {
        return (
            <>
                <HeadMetadata titleKey="award.meta.title" />
                <LayoutComponent page="admin-awards" content={this.renderContent} />
            </>
        );
    }
}
export default injectIntl(AdminAwardPage);

type ParamsType = { id: string };
interface Props extends RouteComponentProps<ParamsType>, WrappedComponentProps {}

interface State {
    users: FamilyMember[];
    award?: Award;
    loading?: boolean;
}
